import {
    Chip,
    Grid,
    FormGroup,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import React from 'react';
import {
    DateField,
    DateTimeField,
    NumberField,
    TextField,
    useChangeable
} from '../Form';
import SelectField from '../Form/fields/SelectField';
import Popup from '../Popup';
import SectionDivider from '../SectionDivider';

import type { ColumnType, FilterValuesType } from './Table';
import { FiltersType } from './tableFilters';
import { formatDate } from '../../../utils/parsing';

type PropsType = {
    filterColumns: ColumnType[],
    filters: FiltersType,
    filterValues: FilterValuesType,
    editFilterKey: string | null,
    setEditFilterKey: (k: string | null) => void,
    setFilterValues: (v: FilterValuesType) => void
}

type FilterFieldType = {
    type?: string,
    options?: {
        label: string,
        value: string
    }[]
    name: string,
    label: string,
    value: string,
    onChange: (v: any) => void,
    isMultiSelect?: boolean,
    exclusive?: boolean,
    disabled?: boolean,
    minDate?: string,
    maxDate?: string
}

function FilterField(props: FilterFieldType) {
    const {
        type,
        options,
        isMultiSelect,
        exclusive,
        ...fieldProps
    } = props;

    if (isMultiSelect && options) {
        const selectedValues = fieldProps.value ? fieldProps.value.split(',').filter(Boolean) : [];

        return (
            <FormGroup>
                {options.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        control={(
                            <Checkbox
                                checked={selectedValues.includes(option.value)}
                                onChange={(e) => {
                                    if (exclusive) {
                                        fieldProps.onChange({
                                            [fieldProps.name]: e.target.checked ? option.value : ''
                                        });
                                        return;
                                    }
                                    const newSelected = e.target.checked
                                        ? [...selectedValues, option.value]
                                        : selectedValues.filter((v) => v !== option.value);
                                    fieldProps.onChange({ [fieldProps.name]: newSelected.join(',') });
                                }}
                                size="small"
                            />
                        )}
                        label={option.label}
                    />
                ))}
            </FormGroup>
        );
    }

    if (options) {
        return (
            <SelectField
                {...fieldProps}
                options={options}
            />
        );
    }
    if (type === 'bool') {
        return (
            <SelectField
                {...fieldProps}
                options={[
                    { label: '', value: '' },
                    { label: 'Ja', value: true },
                    { label: 'Nej', value: false }
                ]}
            />
        );
    }
    if (type === 'number') {
        return (
            <NumberField {...fieldProps} />
        );
    }
    if (type === 'date') {
        return (
            <DateField {...fieldProps} />
        );
    }
    if (type === 'datetime') {
        return (
            <DateTimeField {...fieldProps} />
        );
    }

    return (
        <TextField {...fieldProps} />
    );
}

function hasFilterInput(value: unknown) {
    return value !== '' && value !== null && typeof value !== 'undefined';
}

function publicationRangeActive(columnKey: string, values: { [key: string]: any }) {
    return hasFilterInput(values[`${columnKey}-from`]) || hasFilterInput(values[`${columnKey}-to`]);
}

function publicationEqualsActive(columnKey: string, values: { [key: string]: any }) {
    return hasFilterInput(values[`${columnKey}-equals`]);
}

function ymdKey(value: unknown): string | null {
    if (!hasFilterInput(value)) {
        return null;
    }
    const formatted = formatDate(value);
    return formatted === '-' ? null : formatted;
}

export default function TableFilters({
    filterColumns,
    filters,
    filterValues,
    editFilterKey,
    setEditFilterKey,
    setFilterValues
}: PropsType) {
    const [entity, onChange] = useChangeable(filterValues);

    const cleanFilterValues = React.useCallback((values: { [key: string]: any }) => {
        return Object.fromEntries<any>(Object.entries(values).filter(([, v]) => (
            v !== '' && v !== null && typeof v !== 'undefined'
        )));
    }, []);

    React.useEffect(() => {
        onChange(filterValues, { reset: true });
    }, [filterValues, onChange]);

    const removeFilter = React.useCallback((key: string) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: omit, ...newFilterValues } = filterValues;
        setFilterValues(newFilterValues);
    }, [filterValues, setFilterValues]);

    const isFilterSectionVisible = React.useCallback((col: ColumnType, values: { [key: string]: any } = entity) => {
        if (!col.showWhenFilterIncludes) {
            return true;
        }
        const { columnKey, value } = col.showWhenFilterIncludes;
        const relatedFilter = filters.find((f) => f.columnKey === columnKey);
        if (!relatedFilter) {
            return true;
        }
        const raw = values[relatedFilter.key];
        if (raw === '' || raw === null || typeof raw === 'undefined') {
            return false;
        }
        return String(raw).split(',').includes(value);
    }, [entity, filters]);

    const saveFilters = React.useCallback(() => {
        const merged = { ...entity };
        filterColumns.forEach((col) => {
            if (col.linkedFilters) {
                col.linkedFilters.forEach(({ filterKey, whenValue, value }) => {
                    const colFilterKey = `${col.key}-equals`;
                    const colValue = merged[colFilterKey];
                    const isActive = typeof whenValue === 'undefined'
                        ? colValue !== '' && colValue !== null && typeof colValue !== 'undefined'
                        : colValue === whenValue;
                    if (isActive) {
                        merged[filterKey] = value;
                    }
                });
            }
            if (col.filterType === 'publicationDate') {
                if (publicationEqualsActive(col.key, merged)) {
                    delete merged[`${col.key}-from`];
                    delete merged[`${col.key}-to`];
                } else if (publicationRangeActive(col.key, merged)) {
                    delete merged[`${col.key}-equals`];
                }
            }
            if (col.showWhenFilterIncludes && !isFilterSectionVisible(col, merged)) {
                filters.filter((f) => f.columnKey === col.key).forEach((f) => {
                    delete merged[f.key];
                });
            }
        });
        setFilterValues(cleanFilterValues(merged));
        setEditFilterKey(null);
    }, [cleanFilterValues, entity, filterColumns, filters, isFilterSectionVisible, setEditFilterKey, setFilterValues]);

    const handleFilterChange = React.useCallback((nextValue: { [key: string]: any }, applyDirectly?: boolean) => {
        const patch = { ...nextValue };
        Object.keys(nextValue).forEach((key) => {
            const columnKey = key.replace(/-(equals|from|to)$/, '');
            const col = filterColumns.find((c) => c.key === columnKey);
            if (col?.filterType !== 'publicationDate') {
                return;
            }
            if (key.endsWith('-equals') && hasFilterInput(nextValue[key])) {
                patch[`${columnKey}-from`] = '';
                patch[`${columnKey}-to`] = '';
                return;
            }
            if (key.endsWith('-from') && hasFilterInput(nextValue[key])) {
                patch[`${columnKey}-equals`] = '';
                const from = ymdKey(nextValue[key]);
                const to = ymdKey(entity[`${columnKey}-to`]);
                if (from && to && to < from) {
                    patch[`${columnKey}-to`] = from;
                }
                return;
            }
            if (key.endsWith('-to') && hasFilterInput(nextValue[key])) {
                patch[`${columnKey}-equals`] = '';
                const to = ymdKey(nextValue[key]);
                const from = ymdKey(entity[`${columnKey}-from`]);
                if (from && to && from > to) {
                    patch[`${columnKey}-from`] = to;
                }
            }
        });
        onChange(patch);
        if (applyDirectly) {
            setFilterValues(cleanFilterValues({ ...filterValues, ...patch }));
        }
    }, [cleanFilterValues, entity, filterColumns, filterValues, onChange, setFilterValues]);

    return (
        <>
            <Popup
                open={!!editFilterKey}
                title="Filter"
                okLabel="Spara"
                deleteLabel="Rensa"
                cancelLabel="Avbryt"
                handleOk={saveFilters}
                handleDelete={() => onChange({}, { reset: true })}
                handleClose={() => setEditFilterKey(null)}
            >
                {filterColumns.map((fc, fci) => {
                    const sectionEnabled = isFilterSectionVisible(fc);
                    return (
                        <React.Fragment key={fc.key}>
                            <SectionDivider label={fc.filterSectionLabel || fc.name} marginTop={fci ? 8 : 0} marginBottom={1} />
                            <Grid container spacing={2} sx={{ minWidth: '500px' }}>
                                {filters.filter((f) => f.columnKey === fc.key).map((f) => {
                                    const isPublicationDate = fc.filterType === 'publicationDate';
                                    const equalsKey = `${fc.key}-equals`;
                                    const fromKey = `${fc.key}-from`;
                                    const toKey = `${fc.key}-to`;
                                    const equalsActive = publicationEqualsActive(fc.key, entity);
                                    const rangeActive = publicationRangeActive(fc.key, entity);
                                    const fieldDisabled = !sectionEnabled
                                        || (isPublicationDate && f.key === equalsKey && rangeActive)
                                        || (isPublicationDate && (f.key === fromKey || f.key === toKey) && equalsActive);
                                    const minDate = isPublicationDate && f.key === toKey
                                        ? ymdKey(entity[fromKey]) ?? undefined
                                        : undefined;
                                    const maxDate = isPublicationDate && f.key === fromKey
                                        ? ymdKey(entity[toKey]) ?? undefined
                                        : undefined;
                                    const gridXs = f.isMultiSelect ? 12 : 6;
                                    return (
                                        <React.Fragment key={f.key}>
                                            <Grid item xs={gridXs}>
                                                <FilterField
                                                    type={isPublicationDate ? 'date' : fc.type}
                                                    name={f.key}
                                                    label={f.label}
                                                    value={typeof entity[f.key] === 'undefined' ? '' : entity[f.key]}
                                                    onChange={(nextValue) => handleFilterChange(nextValue, f.applyDirectly)}
                                                    options={f.options}
                                                    isMultiSelect={f.isMultiSelect}
                                                    exclusive={f.exclusive}
                                                    disabled={fieldDisabled}
                                                    minDate={minDate}
                                                    maxDate={maxDate}
                                                />
                                            </Grid>
                                            {isPublicationDate && f.key === equalsKey ? (
                                                <Grid item xs={6} />
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </Grid>
                        </React.Fragment>
                    );
                })}
            </Popup>

            <Grid
                container
                spacing={1}
                justifyContent="flex-start"
                alignItems="flex-start"
                alignContent="flex-end"
                sx={{ pl: 2, pr: 2 }}
            >

                {(
                    Object.entries(filterValues).map(([key, value]) => (
                        <Grid item key={key}>
                            <Chip
                                label={filters.find((f) => f.key === key)?.getDescription(value) ?? '-'}
                                color="secondary"
                                onClick={() => setEditFilterKey(key)}
                                onDelete={() => removeFilter(key)}
                            />
                        </Grid>
                    ))
                )}
            </Grid>
        </>
    );
}
